import type { CONSORS_DOWNLOAD_SUBSCRIPTION_DOCUMENT_REQUEST_FAILURE_REASON } from 'cpool-constants';
import { LOAN_REQUEST_OFFER_SOURCES } from 'cpool-constants';

import { type AcceptedOffer, isEuropaceOffer } from '../../../../types';
import { downloadFile } from '~/utils/downloadFile';

export const getManualOfferDocument = (acceptedOffer: AcceptedOffer) =>
    acceptedOffer.source === LOAN_REQUEST_OFFER_SOURCES.MANUAL && acceptedOffer.documents
        ? acceptedOffer.documents
        : null;

// Bundles multi-document Europace offers into a zip, everything else as a single pdf; throws so the caller can surface failures.
export const downloadOfferDocuments = async (acceptedOffer: AcceptedOffer) => {
    const { bank, id, loanRequestId: aggregateId } = acceptedOffer;
    const manualOfferDocument = getManualOfferDocument(acceptedOffer);
    const bankSuffix = bank ? `-${bank}` : '';

    if (manualOfferDocument) {
        await downloadFile({
            mimeType: manualOfferDocument.mimeType,
            name: `${manualOfferDocument.name}.pdf`,
            route: `/offer?aggregateId=${aggregateId}&key=${manualOfferDocument.key}`,
        });
        return;
    }

    if (isEuropaceOffer(acceptedOffer) && acceptedOffer.documents && acceptedOffer.documents.length > 1) {
        await downloadFile({
            mimeType: 'application/zip',
            name: `Angebot-${id}${bankSuffix}.zip`,
            route: `/offer/download?aggregateId=${aggregateId}&offerId=${id}`,
        });
        return;
    }

    if (
        (isEuropaceOffer(acceptedOffer) && acceptedOffer.documents && acceptedOffer.documents.length === 1) ||
        acceptedOffer.source === LOAN_REQUEST_OFFER_SOURCES.CONSORS
    ) {
        await downloadFile({
            mimeType: 'application/pdf',
            name: `Angebot-${id}${bankSuffix}.pdf`,
            route: `/offer/download?aggregateId=${aggregateId}&offerId=${id}`,
        });
    }
};

// Fallback for a failed zip download: Europace documents have their own urls, so open each in a new tab.
export const openEuropaceDocumentsInNewTabs = (acceptedOffer: AcceptedOffer) => {
    if (!isEuropaceOffer(acceptedOffer) || !acceptedOffer.documents) return;

    // eslint-disable-next-line no-restricted-syntax
    for (const document of acceptedOffer.documents) {
        const newWindow = window.open(document.url, '_blank', 'noopener,noreferrer');
        // Remove the reference to the current window to prevent tabnabbing, phishing attacks.
        if (newWindow) newWindow.opener = null;
    }
};

/** Consors reports the reason for a failed download inside the error message, wrapped in quotes. */
export const getConsorsDownloadFailureReason = (
    acceptedOffer: AcceptedOffer,
    error: unknown,
): CONSORS_DOWNLOAD_SUBSCRIPTION_DOCUMENT_REQUEST_FAILURE_REASON | null => {
    if (acceptedOffer.source !== LOAN_REQUEST_OFFER_SOURCES.CONSORS) return null;
    if (!(error instanceof Error) || !error.message) return null;

    const reason = error.message.match(/"([^"]+)"/)?.[1];
    return (reason as CONSORS_DOWNLOAD_SUBSCRIPTION_DOCUMENT_REQUEST_FAILURE_REASON) ?? null;
};
